import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  message,
} from 'antd';
import {
  ExpenseCategory,
  createExpenseCategory,
  updateExpenseCategory,
} from '../../services/expenseService';
import styles from './DrawerModalMobile.module.css';

const CATEGORY_ICONS = [
  '🍽️', '🚗', '🛒', '📄', '🎮', '🏥', '📚', '💡', '🏠', '👕', '🎬', '✈️', '💊', '🎨', '🏋️', '📱'
];

const CATEGORY_COLORS = [
  '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280',
  '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
];

interface AddCategoriesModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  category?: ExpenseCategory;
  onSuccess?: () => void;
}

export const AddCategoriesModalMobile: React.FC<AddCategoriesModalMobileProps> = ({
  isOpen,
  onClose,
  category,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const isEdit = !!category;

  useEffect(() => {
    if (isOpen) {
      if (category) {
        form.setFieldsValue({
          name: category.name,
          icon: category.icon,
          color: category.color,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          icon: '💰',
          color: '#3B82F6',
        });
      }
    }
  }, [isOpen, category, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEdit && category) {
        await updateExpenseCategory(category.id, values);
        message.success('Cập nhật danh mục thành công');
      } else {
        await createExpenseCategory(values);
        message.success('Thêm danh mục thành công');
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập đầy đủ thông tin');
      } else {
        // Hiển thị thông báo chi tiết từ API
        if (error?.response?.data?.error) {
          message.error(error.response.data.error);
        } else if (error?.message) {
          message.error(error.message);
        } else {
          message.error('Có lỗi xảy ra khi lưu danh mục');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={isEdit ? 'Chỉnh sửa danh mục' : 'Thêm danh mục mới'}
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="85vh"
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div className={styles.drawerFooter}>
          <Button onClick={onClose} className={styles.drawerFooterButton}>
            Hủy
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {isEdit ? 'Cập nhật' : 'Thêm'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          icon: '💰',
          color: '#3B82F6',
        }}
      >
        <Form.Item
          name="name"
          label="Tên danh mục"
          rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          className={styles.formItem}
        >
          <Input placeholder="Nhập tên danh mục" />
        </Form.Item>

        <Form.Item
          name="icon"
          label="Icon"
          className={styles.formItem}
        >
          <Select placeholder="Chọn icon" className={styles.select}>
            {CATEGORY_ICONS.map((icon) => (
              <Select.Option key={icon} value={icon}>
                <div className={styles.iconOption}>
                  <span>{icon}</span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="color"
          label="Màu sắc"
          className={styles.formItem}
        >
          <Select placeholder="Chọn màu sắc" className={styles.select}>
            {CATEGORY_COLORS.map((color) => (
              <Select.Option key={color} value={color}>
                <div className={styles.colorOption}>
                  <div
                    className={styles.colorCircle}
                    style={{ backgroundColor: color }}
                  />
                  <span>{color}</span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
